import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, interval, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ConnectivityService implements OnDestroy {
  static readonly SERVER_UNREACHABLE_MSG =
    'Server not reachable. Please check your network connection and try again.';

  private readonly reachableSubject = new BehaviorSubject<boolean>(true);
  readonly reachable$ = this.reachableSubject.asObservable();

  private readonly cachedDataSubject = new BehaviorSubject<boolean>(false);
  readonly showingCachedData$ = this.cachedDataSubject.asObservable();

  private retrySub?: Subscription;
  private probeSub?: Subscription;

  constructor(private http: HttpClient) {
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', this.onBrowserOffline);
      window.addEventListener('online', this.onBrowserOnline);
      if (!navigator.onLine) {
        this.setUnreachable();
      } else {
        // Defer probe: constructor must not call HttpClient (circular with ConnectivityInterceptor).
        queueMicrotask(() => this.scheduleProbe());
      }
    }
  }

  ngOnDestroy(): void {
    this.stopBackgroundRetry();
    this.probeSub?.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('offline', this.onBrowserOffline);
      window.removeEventListener('online', this.onBrowserOnline);
    }
  }

  get isReachable(): boolean {
    return this.reachableSubject.value;
  }

  get bannerMessage(): string {
    return ConnectivityService.SERVER_UNREACHABLE_MSG;
  }

  setShowingCachedData(show: boolean): void {
    this.cachedDataSubject.next(show);
  }

  setUnreachable(): void {
    if (!this.reachableSubject.value) {
      return;
    }
    this.reachableSubject.next(false);
    this.startBackgroundRetry();
  }

  setReachable(): void {
    if (this.reachableSubject.value) {
      return;
    }
    this.reachableSubject.next(true);
    this.setShowingCachedData(false);
    this.stopBackgroundRetry();
  }

  notifyRequestSuccess(): void {
    this.setReachable();
  }

  notifyRequestFailure(isNetworkError: boolean): void {
    if (isNetworkError) {
      this.setUnreachable();
    }
  }

  static isNetworkError(err: unknown): boolean {
    if (!err || typeof err !== 'object') {
      return false;
    }
    const httpErr = err as { status?: number; statusText?: string; error?: unknown };
    if (httpErr.status === 0) {
      return true;
    }
    if (httpErr.status === 502 || httpErr.status === 503 || httpErr.status === 504) {
      return true;
    }
    const msg = String((httpErr as { message?: string }).message || httpErr.statusText || '').toLowerCase();
    return msg.includes('network') || msg.includes('failed to fetch') || msg.includes('timeout');
  }

  scheduleProbe(): void {
    this.probeSub?.unsubscribe();
    this.probeSub = this.probeServer().subscribe((ok) => {
      if (ok) {
        this.setReachable();
      } else if (!navigator.onLine) {
        this.setUnreachable();
      }
    });
  }

  private probeServer() {
    const url = `${environment.auth_url}/api/health`;
    return this.http.get<{ status?: string }>(url, { responseType: 'json' }).pipe(
      map((body) => body?.status === 'success'),
      tap((ok) => {
        if (ok) {
          this.setReachable();
        }
      }),
      catchError(() => {
        this.setUnreachable();
        return of(false);
      })
    );
  }

  private startBackgroundRetry(): void {
    if (this.retrySub) {
      return;
    }
    this.retrySub = interval(5000)
      .pipe(switchMap(() => this.probeServer()))
      .subscribe();
  }

  private stopBackgroundRetry(): void {
    this.retrySub?.unsubscribe();
    this.retrySub = undefined;
  }

  private onBrowserOffline = (): void => {
    this.setUnreachable();
  };

  private onBrowserOnline = (): void => {
    this.scheduleProbe();
  };
}
